import { ControllerInterface } from "csi/action/Controller";
import JsonResponse from "csi/comm/JsonResponse";
import PostRequest from "csi/comm/PostRequest";
import { CheckboxTreeNode } from "designSystem/components/checkbox/CheckboxTree";
import { ILocaleProvider } from "designSystem/locale";
import csi from "csi/main";
import { SlimCard } from "csi/middleware/card/SlimCard";
import { SlimCardStore } from "csi/middleware/card/SlimCardStore";
import { ControllerSaveFunction } from "csi/middleware/common";
import { C8Node, cnl, ICache } from "csi/typings";
import Search from "csi/xml/Search";
import { runSearch } from "csi/service/dataStoreService";
import FormData from "csi/action/FormData";
import { MassUpdateSharedBOMWizardStore, MassUpdateStyleBOMWizardStore, PartMaterial } from "./MassUpdateBOMsWizardStore";

const MODULE_PRODUCT_SPEC = "ProductSpec";
const BLANCK_SECTION: string = "Blanks";

/** return a Map of Section Stores and a list of PartMaterials */
const transformMaterialsToSectionMap = async (bomRevisionURL: cnl, cache: ICache, locale: ILocaleProvider): Promise<Map<string, CheckboxTreeNode>> => {
	const nodeResults = await getPartMaterials(bomRevisionURL, cache);
	const sectionsMap = new Map<string, CheckboxTreeNode>();
	sectionsMap.set(csi.EMPTY_URL, new CheckboxTreeNode(undefined, csi.EMPTY_URL, BLANCK_SECTION, true));
	nodeResults.forEach((node) => {
		if (node.$Type === "BOMSectionDefinition") {
			const sName = locale.getNodeName(node);
			sectionsMap.set(node.$URL, new CheckboxTreeNode(undefined, node.$URL, sName, true));
		}
	});

	nodeResults
		.filter((node) => node.$Type === "PartMaterial")
		.map((p) => {
			const pName = locale.getNodeName(p);
			const partMaterial = new PartMaterial(undefined, p.$URL, pName, sectionsMap.get(p.DSSection).uniqueId, p.DSSection, true);
			sectionsMap.get(partMaterial.sectionUrl)
				.addChild(partMaterial);
			partMaterial.label = SlimCard({
				displayImage: true,
				slimCardStore: new SlimCardStore(p.$URL, cache),
			});
			return partMaterial;
		});
	//REMOVE the BLANKS if not placements on it
	if (!sectionsMap.get(csi.EMPTY_URL).hasChildren) {
		sectionsMap.delete(csi.EMPTY_URL);
	}
	return sectionsMap;
};

/** Create and initialize MassUpdateStore */
export const createMassUpdateBOMStore = async (
	actionId: string,
	cache: ICache,
	formData: FormData,
	contextNode: C8Node,
	onClose: () => void,
	onSave: ControllerSaveFunction,
	locale: ILocaleProvider,
): Promise<MassUpdateStyleBOMWizardStore> => {
	//In case the action is executed from Theme BOM table row.
	const revisionURL = contextNode?.$Type === "ApparelBOM" ? contextNode.CurrentRevision : contextNode.$URL;

	const sectionsMap = await transformMaterialsToSectionMap(revisionURL, cache, locale);
	let store;
	switch (actionId) {
		case "MassUpdateStyleBOMs":
			store =  new MassUpdateStyleBOMWizardStore(onClose, onSave, actionId, cache, formData, locale, revisionURL, sectionsMap);
			break; 
		case "MassUpdateSharedDataSheets":
			store =  new MassUpdateSharedBOMWizardStore(onClose, onSave, actionId, cache, formData, locale, revisionURL, sectionsMap);
			break; 
		default:
			break;
	}
	return store;
};

/** Query for all PartMaterials and sections of a given bomRevisionURL */
export async function getPartMaterials(bomRevisionURL: cnl, cache: ICache): Promise<C8Node[]> {
	const search = new Search();
	search.setLimitPath(bomRevisionURL, "Child:Items");
	search.addDependencyPaths(["Child:DSSection"]);

	const response = await runSearch(search, cache);
	return response.getNodes();
}

/** Mass Update Style Boms from Theme Bom placements. */
export async function massUpdateStyleBOMs(createRevision: boolean, bomRevisionURL: cnl, pushAllPlacements: boolean, partMaterials: cnl[], styleBoms: cnl[]): Promise<JsonResponse> {
	const request = new PostRequest(MODULE_PRODUCT_SPEC, "MassUpdateStyleBOMs")
		.addParameter("CreateRevision", createRevision)
		.addParameter("URL", bomRevisionURL)
		.addParameter("PartMaterials", partMaterials)
		.addParameter("PushAllPlacements", pushAllPlacements)
		.addParameter("StyleBoms", styleBoms);
	return request.post();
}

/** Mass Update ProductSource BOMs from Style BOMs. */
export async function massUpdateSharedDataSheets(createRevision: boolean, bomRevisionURL: cnl, pushAllPlacements: boolean, partMaterials: cnl[], sourceBoms: cnl[], overrideAttributes: boolean): Promise<JsonResponse> {
	const request = new PostRequest(MODULE_PRODUCT_SPEC, "MassUpdateSharedDataSheets")
		.addParameter("CreateRevision", createRevision)
		.addParameter("URL", bomRevisionURL)
		.addParameter("PartMaterials", partMaterials)
		.addParameter("PushAllPlacements", pushAllPlacements)
		.addParameter("SourceBoms", sourceBoms)
		.addParameter("OverrideAttributes", overrideAttributes);
	return request.post();
}
